import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/features/auth/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Calendar as CalendarPicker } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Client } from '@/features/clients/hooks/useClients';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  Mail, Phone, CalendarIcon, MapPin, AlertTriangle,
  Pill, Baby, Zap, Star, ChevronRight, ChevronLeft,
  Check, Plus, X, Heart, Instagram,
} from 'lucide-react';

// ─── Schema ───────────────────────────────────────────────────────────────────

const clientSchema = z.object({
  first_name: z.string().min(1, 'Prénom requis'),
  last_name: z.string(),
  email: z.union([z.string().email('Email invalide — ex: nom@domaine.com'), z.literal('')]),
  phone: z.string(),
  instagram: z.string(),
  birth_date: z.string(),
  notes: z.string(),
  address: z.object({
    street: z.string(),
    city: z.string(),
    postal_code: z.string(),
    country: z.string(),
  }),
  emergency_contact: z.object({ name: z.string(), phone: z.string() }),
  referral_source: z.string(),
  is_vip: z.boolean(),
  style_preferences: z.array(z.string()),
  allergies: z.array(z.string()),
  conditions: z.array(z.string()),
  medications: z.string(),
  is_pregnant: z.boolean(),
  skin_reactions: z.string(),
  pain_tolerance: z.number().nullable(),
});

export type NewClientData = z.infer<typeof clientSchema>;

const emptyData = (): NewClientData => ({
  first_name: '', last_name: '', email: '', phone: '', instagram: '', birth_date: '', notes: '',
  address: { street: '', city: '', postal_code: '', country: 'France' },
  emergency_contact: { name: '', phone: '' },
  referral_source: '', is_vip: false, style_preferences: [],
  allergies: [], conditions: [], medications: '',
  is_pregnant: false, skin_reactions: '', pain_tolerance: null,
});

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editing: Client | null;
  onCreateClient?: (data: NewClientData) => Promise<void>;
  onUpdateClient?: (id: string, data: NewClientData) => Promise<void>;
}

// ─── DatePickerField ──────────────────────────────────────────────────────────

function DatePickerField({ value, onChange, placeholder = 'Sélectionner une date' }: {
  value: string; onChange: (v: string) => void; placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const selected = value ? new Date(value + 'T12:00:00') : undefined;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button"
          className="w-full flex items-center gap-2 px-3 h-10 rounded-xl border bg-secondary/50 border-border/50 text-sm text-left transition-colors hover:border-primary/50 focus:outline-none focus:ring-2 focus:ring-primary/20">
          <CalendarIcon size={13} className="text-muted-foreground shrink-0" />
          {value
            ? format(new Date(value + 'T12:00:00'), 'dd MMMM yyyy', { locale: fr })
            : <span className="text-muted-foreground">{placeholder}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <CalendarPicker
          mode="single"
          selected={selected}
          onSelect={d => { if (d) { onChange(format(d, 'yyyy-MM-dd')); setOpen(false); } }}
          fromYear={1920}
          toYear={new Date().getFullYear()}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}

// ─── TagInput ─────────────────────────────────────────────────────────────────

function TagInput({ tags, onChange, placeholder }: { tags: string[]; onChange: (t: string[]) => void; placeholder: string }) {
  const [val, setVal] = useState('');
  const add = () => { const v = val.trim(); if (v && !tags.includes(v)) onChange([...tags, v]); setVal(''); };
  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <Input value={val} onChange={e => setVal(e.target.value)} onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), add())}
          placeholder={placeholder} className="bg-secondary/50 border-border/50 h-9 text-sm" />
        <Button type="button" variant="outline" size="sm" onClick={add} className="h-9 border-border/50"><Plus size={13} /></Button>
      </div>
      {tags.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {tags.map(tag => (
            <span key={tag} className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-primary/10 text-primary border border-primary/20">
              {tag}
              <button onClick={() => onChange(tags.filter(t => t !== tag))} className="hover:text-destructive"><X size={10} /></button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── PainDots ─────────────────────────────────────────────────────────────────

function PainDots({ value, onChange }: { value: number | null; onChange: (v: number) => void }) {
  return (
    <div className="flex gap-2">
      {[1, 2, 3, 4, 5].map(n => (
        <button key={n} type="button" onClick={() => onChange(n)}
          className={`w-8 h-8 rounded-full border-2 text-xs font-bold transition-all
            ${(value ?? 0) >= n ? 'bg-primary border-primary text-primary-foreground' : 'border-border/50 text-muted-foreground hover:border-primary/50'}`}>
          {n}
        </button>
      ))}
    </div>
  );
}

// ─── StepIndicator ────────────────────────────────────────────────────────────

function StepIndicator({ current, labels }: { current: number; labels: string[] }) {
  return (
    <div className="flex items-center gap-2 mb-6">
      {labels.map((label, i) => (
        <div key={i} className="flex items-center gap-2 flex-1">
          <div className={`flex items-center gap-2 ${i < labels.length - 1 ? 'flex-1' : ''}`}>
            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 transition-all
              ${i < current ? 'bg-primary text-primary-foreground' : i === current ? 'bg-primary text-primary-foreground ring-2 ring-primary/30 ring-offset-2 ring-offset-background' : 'bg-secondary text-muted-foreground'}`}>
              {i < current ? <Check size={13} /> : i + 1}
            </div>
            <span className={`text-xs font-medium whitespace-nowrap ${i === current ? 'text-foreground' : 'text-muted-foreground'}`}>{label}</span>
            {i < labels.length - 1 && <div className={`flex-1 h-px mx-2 ${i < current ? 'bg-primary' : 'bg-border/50'}`} />}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function ClientFormDialog({ open, onOpenChange, editing, onCreateClient, onUpdateClient }: Props) {
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [duplicate, setDuplicate] = useState<{ id: string; name: string } | null>(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  const { control, register, handleSubmit, trigger, watch, reset, formState: { errors } } = useForm<NewClientData>({
    resolver: zodResolver(clientSchema),
    defaultValues: emptyData(),
  });

  useEffect(() => {
    if (!open) return;
    setStep(0);
    setDuplicate(null);
    if (editing) {
      const addr = (editing.address as Record<string, string>) || {};
      const ec   = (editing.emergency_contact as Record<string, string>) || {};
      reset({
        first_name: editing.first_name || '',
        last_name:  editing.last_name  || '',
        email:      editing.email      || '',
        phone:      editing.phone      || '',
        instagram:  editing.instagram  || '',
        birth_date: editing.birth_date || '',
        notes:      editing.notes      || '',
        address: { street: addr.street || '', city: addr.city || '', postal_code: addr.postal_code || '', country: addr.country || 'France' },
        emergency_contact: { name: ec.name || '', phone: ec.phone || '' },
        referral_source:   editing.referral_source  || '',
        is_vip:            editing.is_vip            || false,
        style_preferences: editing.style_preferences || [],
        allergies: [], conditions: [], medications: '', is_pregnant: false, skin_reactions: '', pain_tolerance: null,
      });
    } else {
      reset(emptyData());
    }
  }, [open, reset, editing]);

  const firstName = watch('first_name');
  const lastName  = watch('last_name');
  const email     = watch('email');

  const handleNext = async () => {
    if (step === 0) {
      const valid = await trigger(['first_name', 'email']);
      if (!valid) return;
      if (!editing) {
        if (duplicate) { setStep(s => s + 1); return; }
        if (user) {
          const fullName = `${firstName.trim()} ${lastName?.trim() ?? ''}`.trim();
          const { data: matches } = await (supabase as any)
            .from('clients').select('id, name').eq('owner_id', user.id).eq('archived', false)
            .or(email ? `email.eq.${email},name.ilike.${fullName}` : `name.ilike.${fullName}`)
            .limit(1);
          if (matches && matches.length > 0) {
            setDuplicate({ id: (matches[0] as any).id, name: (matches[0] as any).name });
            return;
          }
        }
      }
    }
    setStep(s => s + 1);
  };

  const onSubmit = async (data: NewClientData) => {
    setSaving(true);
    try {
      if (editing && onUpdateClient) {
        await onUpdateClient(editing.id, data);
      } else if (onCreateClient) {
        await onCreateClient(data);
      }
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  const handleCreateWithoutHealth = handleSubmit(async (data) => {
    if (!onCreateClient) return;
    setSaving(true);
    try { await onCreateClient(data); onOpenChange(false); }
    finally { setSaving(false); }
  });

  const STEPS = editing ? ['Identité', 'Infos pratiques'] : ['Identité', 'Infos pratiques', 'Santé'];
  const lastStep = editing ? 1 : 2;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl glass-card border-border/50 max-h-[90vh] flex flex-col">
        <DialogHeader className="shrink-0">
          <DialogTitle className="font-display text-lg">{editing ? 'Modifier le client' : 'Nouveau client'}</DialogTitle>
          <DialogDescription className="sr-only">Créer une nouvelle fiche client</DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-1">
          <StepIndicator current={step} labels={STEPS} />

          <AnimatePresence mode="wait">
            <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.18 }}>

              {/* ── Étape 1 — Identité ──────────────────────────────────── */}
              {step === 0 && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">Prénom *</Label>
                      <Input {...register('first_name')} placeholder="Jean" className="bg-secondary/50 border-border/50" autoFocus />
                      {errors.first_name && <p className="text-xs text-destructive">{errors.first_name.message}</p>}
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">Nom</Label>
                      <Input {...register('last_name')} placeholder="Dupont" className="bg-secondary/50 border-border/50" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground flex items-center gap-1"><Mail size={11} /> Email</Label>
                      <Input type="email" {...register('email')} placeholder="jean@email.com"
                        className={`bg-secondary/50 border-border/50 ${errors.email ? 'border-destructive' : ''}`} />
                      {errors.email && <p className="text-xs text-destructive">{errors.email.message}</p>}
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground flex items-center gap-1"><Phone size={11} /> Téléphone</Label>
                      <Input type="tel" {...register('phone')} placeholder="06 00 00 00 00" className="bg-secondary/50 border-border/50" />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground flex items-center gap-1"><Instagram size={11} /> Instagram</Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">@</span>
                      <Input {...register('instagram')} placeholder="votre.compte" className="bg-secondary/50 border-border/50 pl-7" />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground flex items-center gap-1"><CalendarIcon size={11} /> Date de naissance</Label>
                    <Controller control={control} name="birth_date"
                      render={({ field }) => <DatePickerField value={field.value} onChange={field.onChange} placeholder="Sélectionner une date" />} />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground flex items-center gap-1"><MapPin size={11} /> Adresse <span className="text-muted-foreground/50">(optionnel)</span></Label>
                    <Input {...register('address.street')} placeholder="Rue" className="bg-secondary/50 border-border/50" />
                    <div className="grid grid-cols-3 gap-2 mt-1.5">
                      <Input {...register('address.postal_code')} placeholder="Code postal" className="bg-secondary/50 border-border/50 text-sm" />
                      <Input {...register('address.city')} placeholder="Ville" className="bg-secondary/50 border-border/50 text-sm col-span-2" />
                    </div>
                  </div>
                  {duplicate && (
                    <div className="flex items-start gap-3 p-3 rounded-xl bg-amber-500/10 border border-amber-500/30 text-sm">
                      <AlertTriangle size={15} className="text-amber-500 shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-amber-600">Client similaire trouvé : <span className="font-bold">{duplicate.name}</span></p>
                        <p className="text-xs text-muted-foreground mt-0.5">Même email ou même nom. Cliquez sur "Suivant" pour créer quand même, ou ouvrez la fiche existante.</p>
                        <button
                          type="button"
                          className="text-xs text-primary underline mt-1"
                          onClick={() => { onOpenChange(false); navigate(`/clients/${duplicate.id}`); }}
                        >
                          Voir la fiche de {duplicate.name} →
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ── Étape 2 — Infos pratiques ───────────────────────────── */}
              {step === 1 && (
                <div className="space-y-4">
                  <div className="glass-card p-4 space-y-3">
                    <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                      <AlertTriangle size={12} className="text-warning/70" /> Contact d'urgence
                    </h4>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label className="text-xs text-muted-foreground">Nom du contact</Label>
                        <Input {...register('emergency_contact.name')} placeholder="Marie Dupont" className="bg-secondary/50 border-border/50" />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs text-muted-foreground">Téléphone</Label>
                        <Input type="tel" {...register('emergency_contact.phone')} placeholder="06 00 00 00 00" className="bg-secondary/50 border-border/50" />
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">Comment a-t-il connu le studio ?</Label>
                      <Input {...register('referral_source')} placeholder="Instagram, bouche à oreille..." className="bg-secondary/50 border-border/50" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">Statut</Label>
                      <Controller control={control} name="is_vip"
                        render={({ field }) => (
                          <button type="button" onClick={() => field.onChange(!field.value)}
                            className={`w-full flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-medium transition-all
                              ${field.value ? 'bg-warning/10 border-warning/30 text-warning' : 'bg-secondary/50 border-border/50 text-muted-foreground'}`}>
                            <Star size={14} className={field.value ? 'fill-warning' : ''} /> VIP
                          </button>
                        )} />
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">Styles préférés</Label>
                    <Controller control={control} name="style_preferences"
                      render={({ field }) => (
                        <TagInput tags={field.value} onChange={field.onChange} placeholder="Réaliste, Néo-trad, Japonais..." />
                      )} />
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">Notes</Label>
                    <Textarea {...register('notes')} placeholder="Informations complémentaires..." rows={3}
                      className="bg-secondary/50 border-border/50 resize-none" />
                  </div>
                </div>
              )}

              {/* ── Étape 3 — Santé ─────────────────────────────────────── */}
              {step === 2 && (
                <div className="space-y-4">
                  <div className="flex items-start gap-2 p-3 rounded-xl bg-primary/5 border border-primary/10">
                    <Heart size={14} className="text-primary mt-0.5 shrink-0" />
                    <p className="text-xs text-muted-foreground">Ces informations sont optionnelles mais importantes pour la sécurité du client.</p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground flex items-center gap-1">
                        <AlertTriangle size={11} className="text-destructive/70" /> Allergies
                      </Label>
                      <Controller control={control} name="allergies"
                        render={({ field }) => <TagInput tags={field.value} onChange={field.onChange} placeholder="Latex, nickel..." />} />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">Conditions médicales</Label>
                      <Controller control={control} name="conditions"
                        render={({ field }) => <TagInput tags={field.value} onChange={field.onChange} placeholder="Diabète, épilepsie..." />} />
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground flex items-center gap-1"><Pill size={11} /> Médicaments</Label>
                    <Textarea {...register('medications')} placeholder="Anticoagulants, isotrétinoïne..." rows={2}
                      className="bg-secondary/50 border-border/50 resize-none" />
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">Réactions cutanées</Label>
                    <Textarea {...register('skin_reactions')} placeholder="Cicatrisation lente, chéloïdes..." rows={2}
                      className="bg-secondary/50 border-border/50 resize-none" />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground flex items-center gap-1"><Baby size={11} /> Enceinte / Allaitante</Label>
                      <Controller control={control} name="is_pregnant"
                        render={({ field }) => (
                          <button type="button" onClick={() => field.onChange(!field.value)}
                            className={`w-full flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-medium transition-all
                              ${field.value ? 'bg-warning/10 border-warning/30 text-warning' : 'bg-secondary/50 border-border/50 text-muted-foreground'}`}>
                            <Baby size={14} /> {field.value ? 'Oui' : 'Non'}
                          </button>
                        )} />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground flex items-center gap-1"><Zap size={11} /> Tolérance douleur (1→5)</Label>
                      <Controller control={control} name="pain_tolerance"
                        render={({ field }) => <PainDots value={field.value} onChange={field.onChange} />} />
                    </div>
                  </div>
                </div>
              )}

            </motion.div>
          </AnimatePresence>
        </div>

        {/* Footer navigation */}
        <div className="flex items-center justify-between pt-4 border-t border-border/50 shrink-0">
          <Button variant="outline" onClick={() => step === 0 ? onOpenChange(false) : setStep(s => s - 1)}
            className="gap-1.5 border-border/50">
            <ChevronLeft size={15} />
            {step === 0 ? 'Annuler' : 'Précédent'}
          </Button>

          <div className="flex gap-2">
            {step < lastStep ? (
              <>
                {step === 1 && !editing && (
                  <Button variant="outline" onClick={handleCreateWithoutHealth} disabled={saving || !firstName.trim()}
                    className="border-border/50 text-sm">
                    Créer sans infos santé
                  </Button>
                )}
                <Button onClick={handleNext} disabled={!firstName.trim()}
                  className="gap-1.5 bg-primary hover:bg-primary/90 text-primary-foreground">
                  Suivant <ChevronRight size={15} />
                </Button>
              </>
            ) : (
              <Button onClick={handleSubmit(onSubmit)} disabled={saving || !firstName.trim()}
                className="gap-1.5 bg-primary hover:bg-primary/90 text-primary-foreground">
                <Check size={15} /> {saving ? 'Enregistrement...' : (editing ? 'Enregistrer' : 'Créer le client')}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
